<template>
  <div>
    <v-card class="elevation-0 rounded-lg margin-app-summary-card">
      <v-row class="header mx-0 rounded-t-lg">
        <v-col class="px-6 py-0 d-flex align-center">
          <p class="white--text font-20 font-weight-bold ma-0">
            {{ cardTitle }}
          </p>
        </v-col>
      </v-row>
      <v-row class="main mx-0 rounded-b-lg">
        <!-- Desktop -->
        <v-col
          cols="12"
          md="6"
          lg="4"
          xl="3"
          class="py-10 py-md-3 left d-flex justify-center align-center"
        >
          <v-img
            v-if="insurerImage"
            :src="insurerImage"
            min-width="80"
            max-width="170"
          ></v-img>
        </v-col>
        <v-col
          cols="12"
          md="6"
          lg="4"
          xl="6"
          class="middle d-flex justify-center align-center"
        >
          <div>
            <!-- Monthly -->
            <div class="text-center" v-if="ddMonthly">
              <label>{{ monthlyTitle }}</label>
              <h1 class="font-weight-bold">
                {{ selectedQuote?.MonthlyTotal | formatCurrency }}
              </h1>
              <a
                href="#"
                @click.prevent="breakdown('Monthly', selectedQuote)"
                aria-label="Breakdown"
                >Breakdown</a
              >
            </div>
            <!-- Monthly 3 Months Deferred -->
            <div class="text-center" v-else-if="ddDeferred">
              <label>{{ monthlyTitle }}</label>
              <h1 class="font-weight-bold">
                {{
                  selectedQuote?.Monthly3MonthsDeferredTotal | formatCurrency
                }}
              </h1>
              <a
                href="#"
                @click.prevent="breakdown('3Months', selectedQuote)"
                aria-label="Breakdown"
                >Breakdown</a
              >
            </div>
            <!-- Monthly 2 Months Deferred -->
            <div class="text-center" v-else-if="dd2MonthsFreeDeferred">
              <label>{{ monthlyTitle }}</label>
              <h1 class="font-weight-bold">
                {{
                  selectedQuote?.Monthly2FreeMonthsDeferredTotal
                    | formatCurrency
                }}
              </h1>
              <a href="#" @click.prevent="breakdown('2Months', selectedQuote)"
                >Breakdown</a
              >
            </div>
            <!-- Annual -->
            <div class="text-center" v-else>
              <label>{{ annualTitle }}</label>
              <h1 class="font-weight-bold">
                {{ selectedQuote?.AnnualTotal | formatCurrency }}
              </h1>
              <a href="#" @click.prevent="breakdown('Annual', selectedQuote)"
                >Breakdown</a
              >
            </div>
          </div>
        </v-col>
        <v-col
          cols="12"
          lg="4"
          xl="3"
          class="right d-none d-lg-flex justify-center align-center"
        >
          <div class="d-flex flex-column align-center">
            <BaseButton
              text="Share"
              flat
              depressed
              ripple
              class="font-weight-bold px-1 py-3 d-flex"
              icon="mdi-share-variant"
              color="#106175"
              small
              @click="$emit('shareQuote', selectedQuote.QuoteId)"
            />
            <BaseButton
              text="Print"
              flat
              depressed
              ripple
              class="font-weight-bold px-1 py-3 mt-6 d-flex"
              icon="mdi-printer"
              color="#106175"
              small
              @click="$emit('printQuote', selectedQuote)"
            />
          </div>
        </v-col>
      </v-row>
      <!-- Mobile -->
      <v-row class="mx-0 d-flex d-lg-none">
        <v-col xs="6" class="d-flex px-6 justify-center align-center">
          <BaseButton
            text="Share"
            flat
            depressed
            ripple
            class="font-weight-bold px-1 py-3 d-flex"
            icon="mdi-share-variant"
            color="#106175"
            small
            @click="$emit('shareQuote', selectedQuote.QuoteId)"
          />
        </v-col>
        <v-col
          xs="6"
          class="d-flex px-6 justify-center align-center"
          style="border-left: solid 1px transparent"
        >
          <BaseButton
            text="Print"
            flat
            depressed
            ripple
            class="font-weight-bold px-1 py-3 d-flex"
            icon="mdi-printer"
            color="#106175"
            small
            @click="$emit('printQuote', selectedQuote)"
          />
        </v-col>
      </v-row>
    </v-card>

    <v-alert
      v-if="isShowPayByCreditDebitCardMessage"
      color="var(--blue-100)"
      class="margin-app-summary-card-alert"
    >
      <div class="d-flex flex-no-wrap">
        <div class="mr-3">
          <v-icon color="var(--blue-500)">mdi-information</v-icon>
        </div>
        <div>
          <p>
            <span class="font-weight-bold">Important!</span>&nbsp;As you've
            chosen to pay by annual credit/debit card, you've been opted out of
            our annual renewal service. If you would prefer to opt into our
            auto-renewal service, please change your payment method to
            <strong>Direct Debit</strong> above.
          </p>
          <p>
            If you remain opted out of our automatic renewal service, please
            make sure you have adequate insurance cover in place once your
            Paymentshield policy comes to an end.
          </p>
          <p>
            If you own a home with a mortgage, it's often a condition of your
            mortgage agreement to maintain continuous buildings insurance to
            protect your home against serious damage such as fire or flood.
          </p>
        </div>
      </div>
    </v-alert>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import { APPLICATION_MODULE } from "@/store/types/module-types";
import { INSURER_TYPE } from "@/constants/insurer.constants.js";

export default {
  name: "LayoutApplicationSummaryCard",
  components: {
    BaseButton: () => import("@/components/BaseComponents/BaseButton.vue"),
  },
  props: {
    useRetrievedPaymentInfo: {
      type: Boolean,
      default: false,
    },
    showPayByCreditDebitCardMessage: {
      type: Boolean,
      default: true,
    },
    productGroup: {
      type: String,
      default: "",
    },
    cardTitle: {
      type: String,
      default: "Your home insurance quote",
    },
    monthlyTitle: {
      type: String,
      default: "Monthly",
    },
    annualTitle: {
      type: String,
      default: "Annual",
    },
  },
  computed: {
    ...mapGetters(APPLICATION_MODULE, [
      "getRetrievedQuotePaymentFrequency",
      "getRetrievedQuotePaymentMethod",
      "selectedQuote",
    ]),
    ...mapState(APPLICATION_MODULE, ["questionMap"]),
    isShowPayByCreditDebitCardMessage() {
      if (!this.showPayByCreditDebitCardMessage) return false;
      return this.questionMap.PaymentMethod?.Value === "CreditCard";
    },
    insurerImage() {
      return this.insurerImageHelper
        ? require("@/assets/images/insurers/" +
            this.insurerImageHelper +
            ".png")
        : null;
    },
    insurerImageHelper() {
      const insurerName = this.selectedQuote?.InsurerName;
      if (!insurerName) return "";

      return INSURER_TYPE.find(
        (insurer) => insurer.name.toLowerCase() === insurerName.toLowerCase()
      )?.image;
    },
    applicationPaymentFrequency() {
      if (this.useRetrievedPaymentInfo) {
        return this.getRetrievedQuotePaymentFrequency;
      } else {
        return this.questionMap.PaymentFrequency?.Value;
      }
    },
    applicationPaymentMethod() {
      if (this.useRetrievedPaymentInfo) {
        return this.getRetrievedQuotePaymentMethod;
      } else {
        return this.questionMap.PaymentMethod?.Value;
      }
    },
    ddMonthly() {
      return (
        this.applicationPaymentMethod === "DirectDebit" &&
        this.applicationPaymentFrequency === "Monthly"
      );
    },
    ddDeferred() {
      return (
        this.applicationPaymentMethod === "DirectDebit" &&
        this.applicationPaymentFrequency === "Monthly3MonthsDeferred"
      );
    },
    dd2MonthsFreeDeferred() {
      return (
        this.applicationPaymentMethod === "DirectDebit" &&
        this.applicationPaymentFrequency === "Monthly2MonthsFree"
      );
    },
  },
  methods: {
    breakdown(frequency, selectedQuote) {
      const eventName = "showPremiumBreakdown" + frequency;
      this.$emit(eventName, selectedQuote, this.productGroup);
    },
  },
};
</script>

<style lang="scss" scoped>
.margin-app-summary-card {
  margin: 45px 0 12px 0;
}

.header {
  background-color: $black-3;
  height: 48px;
}

.main {
  border-right: solid 1px #7c869a;
  border-bottom: solid 1px #7c869a;
  border-left: solid 1px #7c869a;

  .left {
    border-right: solid 1px #7c869a;
  }

  .middle {
    background-color: $gray-light-5;

    h1 {
      font-size: 40px;
    }
  }

  .right {
    border-left: solid 1px #7c869a;
  }
}

.margin-app-summary-card-alert {
  margin: 45px 0 0 0;
}

// xs to sm breakpoint
@media screen and (max-width: ($grid-breakpoints-sm - 1)) {
  .header {
    height: auto;
    padding: 9px 0;
  }

  .main {
    .left {
      border-right: 0;
      border-bottom: solid 1px #7c869a;
    }

    .middle {
      border-bottom-right-radius: 10px;
      border-bottom-left-radius: 10px;
    }
  }
}

// sm to md breakpoint
@media screen and (min-width: ($grid-breakpoints-sm - 1)) and (max-width: ($grid-breakpoints-md - 1)) {
  .main {
    .middle {
      border-bottom-right-radius: 10px;
      border-bottom-left-radius: 0;
    }
  }
}
</style>
